import { useState, useRef, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const BAR_COUNT = 32;
const SILENCE_THRESHOLD = 15;     // avg amplitude (0-255) below this = silence
const SILENCE_DURATION_MS = 2500; // auto-stop after 2.5 s of continuous silence

/**
 * VoiceSearch – AI-powered voice search with live audio visualisation.
 *
 * Props:
 *   onResults(data)  called with the API response when a search completes
 *   size             'sm' | 'md' (default 'md')
 *   className        extra classes for the trigger button
 */
function VoiceSearch({ onResults, size = 'md', className = '' }) {
  const [state, setState]       = useState('idle'); // idle | recording | processing | error
  const [errorMsg, setErrorMsg] = useState('');
  const [bars, setBars]         = useState(Array(BAR_COUNT).fill(4));
  const [seconds, setSeconds]   = useState(0);

  const recorderRef  = useRef(null);
  const chunksRef    = useRef([]);
  const streamRef    = useRef(null);
  const audioCtxRef  = useRef(null);
  const animFrameRef = useRef(null);
  const timerRef     = useRef(null);
  const autoStopRef  = useRef(null);
  const silenceRef   = useRef(null); // timestamp when silence began
  const stopFnRef    = useRef(null); // always-current ref to stopRecording

  useEffect(() => () => cleanUp(), []); // eslint-disable-line

  const cleanUp = () => {
    cancelAnimationFrame(animFrameRef.current);
    clearInterval(timerRef.current);
    clearTimeout(autoStopRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current?.state !== 'closed') audioCtxRef.current?.close();
  };

  const stopRecording = () => {
    clearTimeout(autoStopRef.current);
    clearInterval(timerRef.current);
    cancelAnimationFrame(animFrameRef.current);
    if (audioCtxRef.current?.state !== 'closed') audioCtxRef.current?.close();
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setState('processing');
  };
  stopFnRef.current = stopRecording; // keep ref current every render

  const startRecording = async () => {
    setErrorMsg('');
    setSeconds(0);
    setBars(Array(BAR_COUNT).fill(4));
    silenceRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // ── Web Audio API for frequency visualisation + silence detection ──────
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx      = new AudioCtx();
      audioCtxRef.current = ctx;
      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = BAR_COUNT * 2;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const draw = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

        // Silence detection — stop automatically if silent for SILENCE_DURATION_MS
        if (avg < SILENCE_THRESHOLD) {
          if (!silenceRef.current) silenceRef.current = Date.now();
          else if (Date.now() - silenceRef.current >= SILENCE_DURATION_MS) {
            stopFnRef.current?.();
            return; // exit animation loop
          }
        } else {
          silenceRef.current = null; // reset on any sound
        }

        setBars(Array.from(dataArray.slice(0, BAR_COUNT)).map((v) => Math.max(4, (v / 255) * 85)));
        animFrameRef.current = requestAnimationFrame(draw);
      };
      draw();

      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);

      // ── MediaRecorder — pick best format Gemini accepts ───────────────────
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => sendAudio(recorder.mimeType);
      recorderRef.current = recorder;
      recorder.start();

      setState('recording');
      autoStopRef.current = setTimeout(() => stopFnRef.current?.(), 15000); // hard cap 15 s
    } catch {
      showError('Microphone access denied');
    }
  };

  const sendAudio = async (mimeType) => {
    try {
      const ext  = mimeType.split('/')[1]?.split(';')[0] || 'webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const fd   = new FormData();
      fd.append('audio', blob, `voice.${ext}`);

      const { data } = await axiosInstance.post('/voice-search', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onResults(data);
      setState('idle');
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? 'Too many searches, please wait'
          : err.response?.data?.message || 'Search failed, try again';
      showError(msg);
    }
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    setState('error');
    setTimeout(() => setState('idle'), 4000);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const isCompact = size === 'sm';

  // ── Mic icon SVG ────────────────────────────────────────────────────────────
  const MicIcon = ({ size: s = 16 }) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );

  return (
    <>
      {/* ── Trigger button ── */}
      <button
        onClick={() => (state === 'idle' || state === 'error') ? startRecording() : null}
        disabled={state === 'processing'}
        aria-label="Search with AI voice"
        className={`
          group relative overflow-hidden flex items-center gap-2 font-semibold text-white
          shadow-lg transition-all duration-300 select-none
          ${isCompact ? 'px-3 py-2 rounded-xl text-xs' : 'px-4 py-2.5 rounded-2xl text-sm'}
          ${state === 'error'
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-gradient-to-r from-brand-300 via-brand-500 to-brand-600 hover:shadow-brand-300/40 hover:shadow-xl hover:scale-[1.03]'}
          ${className}
        `}
      >
        {/* Shimmer sweep on hover */}
        <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" />

        {state === 'error' ? (
          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : (
          <MicIcon size={isCompact ? 14 : 15} />
        )}

        <span className="whitespace-nowrap relative">
          {state === 'error'
            ? (errorMsg.length > 20 ? errorMsg.slice(0, 20) + '…' : errorMsg) || 'Error'
            : isCompact ? 'AI Search' : 'Search with AI'}
        </span>
      </button>

      {/* ── Full-screen overlay ── */}
      {(state === 'recording' || state === 'processing') && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{
            background: 'rgba(21,17,11,0.9)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            touchAction: 'none', // prevent scroll on mobile
          }}
          onClick={state === 'recording' ? stopRecording : undefined}
        >
          {/* Floating blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-96 h-96 rounded-full opacity-10 animate-[spin_20s_linear_infinite]"
              style={{ background: 'radial-gradient(circle,#d4922a 0%,transparent 70%)', top: '-10%', left: '-10%' }} />
            <div className="absolute w-96 h-96 rounded-full opacity-10 animate-[spin_25s_linear_infinite_reverse]"
              style={{ background: 'radial-gradient(circle,#e9c478 0%,transparent 70%)', bottom: '-10%', right: '-10%' }} />
          </div>

          {/* Card */}
          <div
            className="relative flex flex-col items-center gap-6 px-6 py-8 sm:px-10 sm:py-10 rounded-3xl w-[90vw] max-w-sm mx-4"
            style={{
              background: 'rgba(255,255,255,0.045)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 0 80px rgba(212,146,42,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {state === 'recording' ? (
              <>
                {/* Pulsing orb with mic */}
                <div className="relative flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28">
                  <span className="absolute inset-0 rounded-full border-2 border-brand-500/30 animate-ping"
                    style={{ animationDuration: '1.6s' }} />
                  <span className="absolute rounded-full border border-brand-300/20 animate-ping"
                    style={{ inset: '-12px', animationDuration: '2.2s', animationDelay: '0.4s' }} />
                  <div className="w-[4.5rem] h-[4.5rem] sm:w-20 sm:h-20 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,#d4922a 0%,#e9c478 100%)', boxShadow: '0 0 40px rgba(212,146,42,0.6)' }}>
                    <MicIcon size={32} />
                  </div>
                </div>

                {/* Equaliser bars */}
                <div className="flex items-end justify-center gap-[2px] h-12 w-full">
                  {bars.map((h, i) => (
                    <div key={i} className="rounded-full flex-1 min-w-0"
                      style={{
                        height: `${h}%`,
                        transition: 'height 60ms ease',
                        background: 'linear-gradient(to top,#d4922a,#e9c478 60%,#f0b454)',
                        opacity: 0.5 + (h / 100) * 0.5,
                      }} />
                  ))}
                </div>

                {/* Labels */}
                <div className="text-center">
                  <p className="text-white text-base font-semibold tracking-wide">
                    Listening
                    <span className="inline-flex gap-0.5 ml-1">
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="inline-block w-1 h-1 rounded-full bg-brand-300 animate-bounce"
                          style={{ animationDelay: `${i * 0.18}s` }} />
                      ))}
                    </span>
                  </p>
                  <p className="text-white/40 text-xs mt-1 font-mono">{fmt(seconds)}</p>
                  <p className="text-white/30 text-xs mt-0.5">Tap to stop • auto-stops on silence</p>
                </div>

                {/* Stop button — large for mobile touch */}
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 px-8 py-3 rounded-2xl text-sm font-semibold text-white active:scale-95 transition-transform"
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', minWidth: '140px' }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="5" y="5" width="14" height="14" rx="3" />
                  </svg>
                  Stop
                </button>
              </>
            ) : (
              /* ── Processing ── */
              <>
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full animate-spin"
                    style={{ background: 'conic-gradient(from 0deg,#d4922a,#e9c478,#f0b454,#b9772a,#d4922a)', animationDuration: '2s' }} />
                  <div className="absolute rounded-full flex items-center justify-center"
                    style={{ inset: '4px', background: 'rgba(21,17,11,0.95)' }}>
                    <svg className="w-10 h-10 animate-[pulse_1.5s_ease-in-out_infinite]" viewBox="0 0 24 24" fill="url(#vs-grad2)">
                      <defs>
                        <linearGradient id="vs-grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#e9c478" />
                          <stop offset="100%" stopColor="#f0b454" />
                        </linearGradient>
                      </defs>
                      <path d="M12 2l1.8 5.4L19.2 9l-5.4 1.8L12 16.2l-1.8-5.4L4.8 9l5.4-1.8z" />
                      <path d="M19.5 15.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z" opacity=".7" />
                    </svg>
                  </div>
                </div>

                <div className="text-center space-y-1.5">
                  <p className="text-white text-base font-semibold">AI is analyzing…</p>
                  <p className="text-white/45 text-sm">Finding your perfect lenses</p>
                  <div className="flex items-center justify-center gap-1.5 pt-1">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ background: 'linear-gradient(to bottom,#e9c478,#d4922a)', animationDelay: `${i * 0.18}s` }} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VoiceSearch;
